import { Box, Button, Card, Chip, Container, Stack, Typography, alpha, useTheme } from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import Iconify from "components/Iconify";
import Page from "components/Page";
import { usePromptsQuery } from "hooks/prompts/usePromptsQueries";
import { useSearchByKeys } from "hooks/useSearch";
import useSettings from "hooks/useSettings";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PATH_DASHBOARD } from "routes/paths";
import { useDebounce } from "use-debounce";

import { AVAILABLE_TAGS } from "@/pages/dashboard/prompts/constants/prompts";

import { getDisplayTags, getTagColor } from "../utils/promptsUtils";
import CustomSearchBar from "./custom-search-bar";

const PromptsList = () => {
    const { t } = useTranslation();
    const { themeStretch } = useSettings();
    const navigate = useNavigate();
    const theme = useTheme();

    // API Query
    const { data = [], isLoading, error } = usePromptsQuery();

    // Error handling
    if (error) {
        console.error("Error loading prompts:", error);
    }

    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText] = useDebounce(searchText, 400);

    const filteredData = useSearchByKeys(debouncedSearchText, data, ["name", "prompt", "tags"]);

    const handleEditRow = (row) => {
        navigate(`${PATH_DASHBOARD.general.prompts}/form/${row.id}`, {
            state: { prompt: row },
        });
    };

    // const handleDeleteRow = (row) => {
    //     // TODO: Add delete functionality with confirmation dialog
    //     console.log("Delete prompt:", row);
    // };

    const handleAddPrompt = () => {
        navigate(`${PATH_DASHBOARD.general.prompts}/form`);
    };

    const handleClearSearch = () => {
        setSearchText("");
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: t("name"),
            flex: 1,
            minWidth: 250,
            renderCell: (params) => {
                const displayTags = getDisplayTags(params.row.tags);
                return (
                    <Box sx={{ py: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {params.value}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {displayTags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    color={getTagColor(tag)}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Stack>
                    </Box>
                );
            },
        },
        {
            field: "prompt",
            headerName: t("content"),
            flex: 2,
            minWidth: 400,
            renderCell: (params) => (
                <Box
                    sx={{
                        py: 1,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        minHeight: 60,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            lineHeight: 1.4,
                        }}
                    >
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "updatedAt",
            headerName: t("date"),
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => (
                <Box sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {new Date(params.value).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>
                        {new Date(params.value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "actions",
            type: "actions",
            headerName: t("actions"),
            flex: 0.5,
            minWidth: 100,
            cellClassName: "actions",
            getActions: ({ row }) => {
                return [
                    <GridActionsCellItem
                        key={`edit-${row.id}`}
                        icon={
                            <Box
                                sx={{
                                    p: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    "&:hover": {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                }}
                            >
                                <Iconify icon={"solar:pen-bold"} sx={{ color: "primary.main", fontSize: "1.2em" }} />
                            </Box>
                        }
                        label={t("edit")}
                        onClick={() => handleEditRow(row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={`delete-${row.id}`}
                        icon={
                            <Box
                                sx={{
                                    p: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    opacity: 0.5,
                                    cursor: "not-allowed",
                                }}
                            >
                                <Iconify
                                    icon={"solar:trash-bin-trash-bold"}
                                    sx={{ color: "error.main", fontSize: "1.2em" }}
                                />
                            </Box>
                        }
                        label={t("delete")}
                        disabled={true}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <Page title={t("prompts")}>
            <Container maxWidth={themeStretch ? false : "xl"}>
                {/* Header Section */}
                <Box sx={{ mb: 2.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography variant="h3" component="h1" sx={{ mb: 0.5 }}>
                                {t("prompts")}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {t("manage_and_organize_your_ai_prompts_for_different_use_cases")}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Iconify icon="solar:add-circle-bold" />}
                            onClick={handleAddPrompt}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                                },
                                transition: "all 0.3s ease",
                            }}
                        >
                            {t("new_prompt")}
                        </Button>
                    </Stack>
                </Box>

                {/* Stats Cards */}
                <Box sx={{ mb: 2.5 }}>
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            overflowX: "auto",
                            pb: 1,
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                            "&:hover": {
                                scrollbarWidth: "thin",
                                "&::-webkit-scrollbar": {
                                    display: "block",
                                    height: 6,
                                },
                                "&::-webkit-scrollbar-track": {
                                    backgroundColor: alpha(theme.palette.grey[500], 0.1),
                                    borderRadius: 3,
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: alpha(theme.palette.grey[500], 0.3),
                                    borderRadius: 3,
                                    "&:hover": {
                                        backgroundColor: alpha(theme.palette.grey[500], 0.4),
                                    },
                                },
                            },
                        }}
                    >
                        {/* Total Prompts Card */}
                        <Card
                            sx={{
                                p: 2,
                                minWidth: 200,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    }}
                                >
                                    <Iconify
                                        icon="solar:document-text-bold"
                                        sx={{ fontSize: 24, color: "primary.main" }}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {data.length}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        {t("total_prompts")}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>

                        {/* Dynamic Tag Cards */}
                        {AVAILABLE_TAGS.map((tag) => {
                            // Special handling for default tag
                            const count =
                                tag === "default"
                                    ? data.filter((p) => !p.tags || p.tags.length === 0 || p.tags.includes("default"))
                                          .length
                                    : data.filter((p) => p.tags?.includes(tag)).length;

                            // Tag-specific theme colors and icons
                            const tagConfig = {
                                medical: {
                                    color: theme.palette.error,
                                    icon: "solar:medical-kit-bold",
                                },
                                support: {
                                    color: theme.palette.info,
                                    icon: "solar:headphones-round-sound-bold",
                                },
                                analysis: {
                                    color: theme.palette.primary,
                                    icon: "solar:chart-2-bold",
                                },
                                creative: {
                                    color: theme.palette.secondary,
                                    icon: "solar:palette-2-bold",
                                },
                                technical: {
                                    color: theme.palette.success,
                                    icon: "solar:code-bold",
                                },
                                general: {
                                    color: theme.palette.warning,
                                    icon: "solar:document-text-bold",
                                },
                                default: {
                                    color: { main: theme.palette.grey[500] },
                                    icon: "solar:file-text-bold",
                                },
                            };

                            const config = tagConfig[tag as keyof typeof tagConfig];

                            return (
                                <Card
                                    key={tag}
                                    sx={{
                                        p: 2,
                                        minWidth: 200,
                                        background: `linear-gradient(135deg, ${alpha(config.color.main, 0.1)} 0%, ${alpha(config.color.main, 0.05)} 100%)`,
                                        border: `1px solid ${alpha(config.color.main, 0.1)}`,
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                backgroundColor: alpha(config.color.main, 0.1),
                                            }}
                                        >
                                            <Iconify
                                                icon={config.icon}
                                                sx={{ fontSize: 24, color: config.color.main }}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {count}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "text.secondary", textTransform: "capitalize" }}
                                            >
                                                {tag} Prompts
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Card>
                            );
                        })}
                    </Stack>
                </Box>

                {/* Custom Search Bar */}
                <CustomSearchBar
                    value={searchText}
                    onChange={setSearchText}
                    onClear={handleClearSearch}
                    placeholder={t("search_prompts_by_name_content_or_category")}
                    filteredCount={filteredData.length}
                />

                {/* Main Content Card */}
                <Card
                    sx={{
                        borderRadius: 3,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.grey[500], 0.12)}`,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                        overflow: "hidden",
                    }}
                >
                    <DataGrid
                        loading={isLoading}
                        rows={filteredData}
                        columns={columns}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        rowHeight={80}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 4, page: 0 },
                            },
                        }}
                        pageSizeOptions={[4, 8, 12]}
                        pagination
                        sx={{
                            border: "none",
                            height: 430,
                            "& .MuiDataGrid-root": {
                                border: "none",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: alpha(theme.palette.grey[500], 0.05),
                                borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                },
                            },
                            "& .MuiDataGrid-row": {
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                                "&.Mui-selected": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    "&:hover": {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                    },
                                },
                            },
                            "& .MuiDataGrid-cell": {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.05)}`,
                            },
                            "& .MuiDataGrid-footerContainer": {
                                borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
                                backgroundColor: alpha(theme.palette.grey[500], 0.02),
                            },
                        }}
                    />
                </Card>

                {/* Bottom spacing */}
                <Box sx={{ height: 40 }} />
            </Container>
        </Page>
    );
};

export default PromptsList;
